import { VenueOS, type VenueOSClient, type VenueOSContext } from '@mappedin/venueos-applet';
import type { MutableRefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

const INIT_TIMEOUT_MS = 10_000;

export interface UseVenueOSReturn {
  client: VenueOSClient | null;
  clientRef: MutableRefObject<VenueOSClient | null>;
  context: VenueOSContext | null;
  isEmbedded: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Initialize the VenueOS applet SDK. Mirrors the pattern in
 * `apps/transit-app/src/hooks/useVenueOS.ts`: if the applet isn't
 * embedded in a VenueOS host we resolve immediately with a null client
 * so the caller can render a "must be embedded" fallback.
 */
export function useVenueOS(): UseVenueOSReturn {
  const clientRef = useRef<VenueOSClient | null>(null);
  const contextHandlerRef = useRef<((ctx: VenueOSContext) => void) | null>(null);
  const [context, setContext] = useState<VenueOSContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isEmbedded = VenueOS.isEmbedded();

  useEffect(() => {
    let mounted = true;

    const initVenueOS = async (): Promise<void> => {
      if (!isEmbedded) {
        setIsLoading(false);
        setContext(null);
        return;
      }

      try {
        const client = await VenueOS.init({ timeout: INIT_TIMEOUT_MS });
        if (!mounted || !client) return;

        clientRef.current = client;
        setContext(client.context);

        const handler = (newContext: VenueOSContext): void => {
          if (mounted) setContext(newContext);
        };
        contextHandlerRef.current = handler;
        client.on('context:updated', handler);
        setIsLoading(false);
      } catch (err: unknown) {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : 'Failed to initialize VenueOS');
        setContext(null);
        setIsLoading(false);
      }
    };

    void initVenueOS();

    return () => {
      mounted = false;
      const client = clientRef.current;
      if (!client) return;
      const handler = contextHandlerRef.current;
      if (handler) {
        client.off('context:updated', handler);
        contextHandlerRef.current = null;
      }
      client.destroy();
      clientRef.current = null;
    };
  }, [isEmbedded]);

  return {
    client: clientRef.current,
    clientRef,
    context,
    isEmbedded,
    isLoading,
    error,
  };
}
